import React, { useCallback, useEffect, useMemo } from 'react';

import type {
	ActivityPanelState,
	QueryPanelState,
	SidePanelAction,
} from '@atlassian/audit-logs-common';

import { SidePanelContext } from './context';
import type { SidePanelContextValue, SidePanelControllerProps } from './types';
import { sidePanelReducer } from './utils';

/**
 * SidePanelController is the central orchestration point for side panel state management.
 *
 * It:
 * - Holds the reducer logic for pure state transitions
 * - Orchestrates side effects and external state updates
 * - Provides context to child components via useSidePanel hook
 *
 * The consumer passes in the external state and action handlers.
 * Child components use the useSidePanel hook to access state and dispatch actions.
 */
export const SidePanelController = ({ state, actions, children }: SidePanelControllerProps) => {
	// Centralized effects / derived logic live here
	// Examples: tab normalization, analytics, validation
	useEffect(() => {
		if (!state) {
			return;
		}

		// You can add effects here such as:
		// - Fire analytics when tab changes
		// - Validate activeTab is valid for the current mode
		// - Perform data fetching when mode/data changes
	}, [state]);

	// Dispatch function orchestrates external updates based on reducer result
	const dispatch = useCallback(
		(action: SidePanelAction) => {
			const current = state;
			const next = sidePanelReducer(current, action);

			// Orchestrate external updates based on reducer result
			switch (action.type) {
				case 'SET_TAB': {
					if (!current || !next) {
						break;
					}

					// Call appropriate external handler based on mode
					if (current.mode === 'activity-details') {
						actions.setActivityTab((next as ActivityPanelState).activeTab);
					} else {
						actions.setQueryTab((next as QueryPanelState).activeTab);
					}
					break;
				}

				case 'CLOSE': {
					actions.onClose();
					break;
				}
			}
		},
		[state, actions],
	);

	// Memoize context value to prevent unnecessary re-renders
	const value = useMemo<SidePanelContextValue>(() => ({ state, dispatch }), [state, dispatch]);

	return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};
